// utils/planAccess.ts
//
// Who can open what, by plan. The only file that holds these rules.
//
//   Free   → every tab of its own region, except Energy Match (paid add-on, Pro or Global)
//   Pro    → every tab of its own region
//   Global → every tab of every region
//
// The backend enforces the same rules (backend/helper/planAccess.js); this decides what the UI shows.
// An expired or cancelled subscription is Free, whatever `plan` still says.

export type Plan = "free" | "pro" | "global";

interface PlanUser {
  plan?: string;
  region?: string;
  roleId?: number;
  unlockedRegions?: string[];
  allRegionsApproved?: boolean;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  subscriptionId?: string;
  energyMatchAccess?: { active?: boolean; expiresAt?: string | Date };
}

const TESTER_ROLE_ID = 3;

const normalize = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "");

const PAID_STATUSES = new Set(["active", "trialing"]);

/** True while the subscription is live and its end date has not passed. */
const isPaid = (user: PlanUser): boolean => {
  if (!user.subscriptionId) return false;
  if (!PAID_STATUSES.has(String(user.subscriptionStatus || "").toLowerCase())) return false;
  const end = user.subscriptionEndDate ? new Date(user.subscriptionEndDate).getTime() : NaN;
  return Number.isNaN(end) || end >= Date.now();
};

export const getUserPlan = (user: PlanUser | null | undefined): Plan => {
  if (!user) return "free";
  if (user.region === "all" && user.allRegionsApproved) return "global";
  if (!isPaid(user)) return "free";
  // A stored "free" is the database default, so a live subscription is at least Pro.
  return user.plan === "global" ? "global" : "pro";
};

/** True while the user holds a paid, unexpired Energy Match™ add-on. */
const hasEnergyMatchAddon = (user: PlanUser): boolean => {
  const access = user.energyMatchAccess;
  if (!access?.active) return false;
  const end = access.expiresAt ? new Date(access.expiresAt).getTime() : NaN;
  return Number.isNaN(end) || end >= Date.now();
};

/** True when a tab or category name is the Energy Match feature. */
export const isEnergyMatchName = (name: string): boolean => normalize(name).includes("energymatch");

/** True when this tab is locked for the user: only Energy Match, and only on the Free plan. */
export const isTabLocked = (user: PlanUser | null | undefined, tabName: string): boolean => {
  if (!user || user.roleId === TESTER_ROLE_ID) return false;
  if (!isEnergyMatchName(tabName)) return false;
  return getUserPlan(user) === "free" && !hasEnergyMatchAddon(user);
};

/** Pro is limited to its own (or bought) regions; Global can use every region. */
export const canUseRegion = (user: PlanUser | null | undefined, region: string): boolean => {
  if (getUserPlan(user) === "global") return true;
  return region === (user?.region || "healjai") || !!user?.unlockedRegions?.includes(region);
};

/** True when the server refused because the region is not open for this user. */
export const isRegionLocked = (error: any): boolean =>
  error?.response?.data?.code === "REGION_LOCKED";
